'use client';

import { useState, useEffect, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Announcement } from '@/types/serve-finder';
import { Img, useAssetUrl, useBgUrl } from '@/lib/asset-url';
import { BaseCard } from './BaseCard';
import { CardHero } from './CardHero';
import { CardCTA } from './CardCTA';

interface AnnouncementCardProps {
  announcement: Announcement;
}

/**
 * A simple link-out card for general announcements (giving, new here, groups, etc.).
 * Clicks open the URL directly — no detail sheet.
 */
export function AnnouncementCard({ announcement }: AnnouncementCardProps) {
  const theme = announcement.Card_Theme;
  const hasTheme = !!theme;
  const hasSlider = announcement.Slider_Slides && announcement.Slider_Slides.length > 0;
  const resolve = useAssetUrl();
  const bgUrl = useBgUrl();

  // Video background card (e.g. Giving)
  const videoRef = useRef<HTMLVideoElement>(null);

  if (announcement.BG_Is_Video) {
    return (
      <BaseCard
        variant="full-bleed"
        as="a"
        href={announcement.URL}
        style={{ backgroundColor: '#111' }}
        onMouseEnter={() => videoRef.current?.play()}
        onMouseLeave={() => videoRef.current?.pause()}
        onFocus={() => videoRef.current?.play()}
        onBlur={() => videoRef.current?.pause()}
        bgElement={
          <>
            <video
              ref={videoRef}
              src={resolve(announcement.Image_URL)}
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-black/65" />
          </>
        }
        body={
          <div className="relative flex flex-col items-center justify-center px-6 py-10 text-center">
            <h3
              className="text-4xl leading-[1.05] font-normal whitespace-pre-line sm:text-5xl"
              style={{
                color: theme?.Accent_Color ?? '#2fbfaf',
                fontFamily: 'Georgia, "Times New Roman", serif',
              }}
            >
              {announcement.Title}
            </h3>
            {announcement.Description && (
              <p
                className="mt-6 max-w-[85%] text-lg leading-relaxed"
                style={{ color: theme?.Body_Color ?? 'rgba(255,255,255,0.8)' }}
              >
                {announcement.Description}
              </p>
            )}
          </div>
        }
        footer={
          <CardCTA
            label={announcement.CTA_Label}
            icon="chevron"
            accentColor={theme?.Accent_Color ?? '#2fbfaf'}
            borderColor={theme?.Border_Color ?? 'rgba(255,255,255,0.15)'}
            className="relative"
          />
        }
      />
    );
  }

  // Slider card (e.g. New Here?)
  if (hasSlider) {
    return <SliderAnnouncementCard announcement={announcement} />;
  }

  const cardStyle: React.CSSProperties = hasTheme
    ? {
        backgroundColor: theme!.Body_BG,
        backgroundImage: theme!.Body_BG_Image ? bgUrl(theme!.Body_BG_Image) : undefined,
        backgroundSize: theme!.Body_BG_Image ? 'cover' : undefined,
        backgroundPosition: theme!.Body_BG_Image ? 'center' : undefined,
      }
    : {};

  // Themed card with BG image — full-card layout (no hero split)
  if (hasTheme && theme!.Body_BG_Image) {
    return (
      <BaseCard
        variant="full-bleed"
        as="a"
        href={announcement.URL}
        style={cardStyle}
        body={
          <div className="flex flex-col justify-end p-5">
            <h3
              className="text-lg leading-tight font-extrabold"
              style={{ color: theme!.Title_Color }}
            >
              {announcement.Title}
            </h3>
            {announcement.Subtitle && (
              <p
                className="mt-0.5 text-xs font-semibold tracking-wider uppercase"
                style={{ color: theme!.Subtitle_Color }}
              >
                {announcement.Subtitle}
              </p>
            )}
            <p className="mt-2 text-sm leading-relaxed" style={{ color: theme!.Body_Color }}>
              {announcement.Description}
            </p>
          </div>
        }
        footer={<CardCTA label={announcement.CTA_Label} icon="chevron" theme={theme} />}
      />
    );
  }

  // Standard layout: hero image + body
  return (
    <BaseCard
      variant="standard"
      as="a"
      href={announcement.URL}
      className="bg-card"
      style={hasTheme ? { backgroundColor: theme!.Body_BG } : undefined}
      header={
        <CardHero bgUrl={announcement.Image_URL}>
          {/* Override the default gradient for announcements */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent" />
        </CardHero>
      }
      body={
        <div className="p-4 pb-3">
          <h3
            className="text-base leading-tight font-extrabold"
            style={{ color: theme?.Title_Color ?? undefined }}
          >
            <span className={hasTheme ? '' : 'text-foreground'}>{announcement.Title}</span>
          </h3>
          {announcement.Subtitle && (
            <p
              className="mt-0.5 text-xs font-semibold tracking-wider uppercase"
              style={{ color: theme?.Subtitle_Color ?? undefined }}
            >
              <span className={hasTheme ? '' : 'text-muted-foreground'}>
                {announcement.Subtitle}
              </span>
            </p>
          )}
          <p
            className="mt-2 text-sm leading-relaxed"
            style={{ color: theme?.Body_Color ?? undefined }}
          >
            <span className={hasTheme ? '' : 'text-muted-foreground'}>
              {announcement.Description}
            </span>
          </p>
        </div>
      }
      footer={
        <CardCTA
          label={announcement.CTA_Label}
          icon="chevron"
          theme={theme}
          useDefaultColors={!hasTheme}
        />
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Slider Announcement Card — synced image + text crossfade carousel
// ---------------------------------------------------------------------------

function SliderAnnouncementCard({ announcement }: { announcement: Announcement }) {
  const slides = announcement.Slider_Slides!;
  const [currentIndex, setCurrentIndex] = useState(0);
  const resolve = useAssetUrl();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const slide = slides[currentIndex];

  return (
    <BaseCard
      variant="standard"
      as="a"
      href={announcement.URL}
      className="bg-card"
      header={
        <div className="relative aspect-[16/9] w-full overflow-hidden">
          <AnimatePresence mode="popLayout">
            <motion.img
              key={slide.Image_URL}
              src={resolve(slide.Image_URL)}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: 'easeInOut' }}
            />
          </AnimatePresence>

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent" />

          {/* "I'm New" badge — green highlight, top-left */}
          <div className="absolute top-4 left-4 z-10">
            <span
              className="inline-block px-2.5 py-1 text-sm font-extrabold tracking-wide text-white uppercase"
              style={{ backgroundColor: '#6abf4b' }}
            >
              I&apos;m New
            </span>
          </div>

          {/* Slide indicators — top-right */}
          <div className="absolute top-4 right-4 z-10 flex gap-1.5">
            {slides.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === currentIndex ? 'w-5 bg-[#6abf4b]' : 'w-1.5 bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
      }
      body={
        <div className="relative flex flex-col p-4 pb-3" style={{ minHeight: 120 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="absolute inset-x-4 top-4"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
            >
              <h3 className="text-foreground text-base leading-tight font-extrabold capitalize">
                {slide.Heading}
              </h3>
              <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">{slide.Blurb}</p>
            </motion.div>
          </AnimatePresence>
        </div>
      }
      footer={<CardCTA label={announcement.CTA_Label} icon="chevron" useDefaultColors />}
    />
  );
}
